import { getSessionStorage } from '@shared/core';
import { seededUnit } from '@apps/card/utils/seededRandom';
import { useCallback } from 'react';

interface CachedRandom {
  deps: unknown[];
  /**
   * Derive values from this seed instead of `Math.random`, so the server and the client agree
   * (the public gallery is server-rendered, PRNT-5494). Without it, a value is rolled once per
   * session and kept in session storage.
   */
  seed?: string;
}

const ss = getSessionStorage();

export const useCachedRandom = (args: CachedRandom) => {
  const { deps, seed } = args;

  const get = useCallback(
    (key: string | number) => {
      if (seed !== undefined) return seededUnit(seed, key);

      const currentValue = ss.getItem(String(key));

      if (currentValue) {
        return parseFloat(currentValue);
      }

      const random = Math.random();

      ss.setItem(String(key), String(random));

      return random;
    },
    // Spread, not nested: callers pass an inline array, which as a single dependency would be new
    // on every render and hand every consumer a new function.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [...deps, seed],
  );

  return get;
};
